import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../utils/api'
import toast from 'react-hot-toast'

const PopupEditor = () => {
  const navigate = useNavigate()
  const { user } = useAuth()

  // Helper function to get full image URL with cache busting
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null
    // If it's already a full URL, use it directly
    if (imageUrl.startsWith('http')) {
      const timestamp = Date.now()
      return `${imageUrl}?t=${timestamp}`
    }
    // If it's a relative path, construct the full URL
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001'
    const timestamp = Date.now()
    return `${baseUrl}${imageUrl}?t=${timestamp}`
  }
  
  const [popupElements, setPopupElements] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [editingElements, setEditingElements] = useState({}) // Track which elements are being edited
  const [savingElements, setSavingElements] = useState({}) // Track which elements are being saved

  useEffect(() => {
    fetchData()
  }, [])

  // Functions to manage edit state
  const toggleEdit = (elementId) => {
    setEditingElements(prev => ({
      ...prev,
      [elementId]: !prev[elementId]
    }))
  }

  const isEditing = (elementId) => {
    return editingElements[elementId] || false
  }

  const isSaving = (elementId) => {
    return savingElements[elementId] || false
  }

  const fetchData = async () => {
    try {
      const response = await api.get('/popups')
      console.log('Fetched popup elements:', response.data.popupElements)
      // Log ALL elements and their imageUrl status
      response.data.popupElements?.forEach(element => {
        console.log(`Element ${element.id} (${element.name}):`, {
          imageUrl: element.imageUrl,
          hasImage: !!element.imageUrl,
          fullImageUrl: element.imageUrl ? getImageUrl(element.imageUrl) : 'NO IMAGE'
        })
      })
      setPopupElements(response.data.popupElements || [])
    } catch (error) {
      console.error('Failed to fetch data:', error)
      toast.error('Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  // Optimize image before upload
  const optimizeImage = (file, maxWidth = 800, maxHeight = 800, quality = 0.85) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      
      reader.onload = (e) => {
        const img = new Image()
        
        img.onload = () => {
          // Calculate new dimensions while maintaining aspect ratio
          let width = img.width
          let height = img.height
          
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height)
            width = width * ratio
            height = height * ratio
          }
          
          // Create canvas and resize
          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          const ctx = canvas.getContext('2d')
          
          // Use high-quality image rendering
          ctx.imageSmoothingEnabled = true
          ctx.imageSmoothingQuality = 'high'
          ctx.drawImage(img, 0, 0, width, height)
          
          // Convert to blob with compression
          canvas.toBlob(
            (blob) => {
              if (blob) {
                // Create a new File object with optimized image
                const optimizedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                })
                resolve(optimizedFile)
              } else {
                reject(new Error('Failed to optimize image'))
              }
            },
            'image/jpeg',
            quality
          )
        }
        
        img.onerror = () => reject(new Error('Failed to load image'))
        img.src = e.target.result
      }
      
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsDataURL(file)
    })
  }

  const handleImageUpload = async (file, elementId) => {
    setUploading(true)
    try {
      // Optimize image before upload
      toast.loading('Optimizing image...', { id: 'optimizing' })
      const optimizedFile = await optimizeImage(file, 800, 800, 0.85)
      toast.dismiss('optimizing')
      
      const formData = new FormData()
      formData.append('image', optimizedFile)

      toast.loading('Uploading optimized image...', { id: 'uploading' })
      const response = await api.post('/popups/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      toast.dismiss('uploading')

      if (elementId && response.data.imageUrl) {
        console.log('Image upload response:', response.data.imageUrl)
        console.log('Updating element locally with imageUrl:', response.data.imageUrl)
        
        // Get the current element data before updating locally
        const currentElement = popupElements.find(el => el.id === elementId)
        if (!currentElement) {
          throw new Error('Element not found')
        }
        
        // Update the element with the image URL locally
        updateElementLocally(elementId, { imageUrl: response.data.imageUrl })
        
        // Automatically save the element to persist the image URL to the database
        const updates = {
          name: currentElement.name,
          title: currentElement.title,
          socialHandle: currentElement.socialHandle,
          description: currentElement.description,
          websiteUrl: currentElement.websiteUrl,
          imageUrl: response.data.imageUrl
        }

        await api.put(`/popups/${elementId}`, updates)
        console.log('Element automatically saved with new image URL')
        
        toast.success('Image uploaded and saved successfully')
      } else {
        console.log('Image upload response missing data:', {
          elementId,
          imageUrl: response.data?.imageUrl,
          fullResponse: response.data
        })
      }

      return response.data.imageUrl
    } catch (error) {
      console.error('Failed to upload image:', error)
      toast.dismiss('optimizing')
      toast.dismiss('uploading')
      
      if (error.message && error.message.includes('optimize')) {
        toast.error('Failed to optimize image. Please try a different image.')
      } else {
        toast.error('Failed to upload image')
      }
      return null
    } finally {
      setUploading(false)
    }
  }

  const createElement = async (elementType) => {
    try {
      // Set default names based on element type
      const defaultNames = {
        'HOST': 'New Host',
        'PROMO': 'New Promotion', 
        'SHOP': 'Shop Item',
        'SOCIAL': 'Social Media'
      }

      const response = await api.post('/popups', {
        elementType,
        name: defaultNames[elementType] || 'New Element',
        title: '',
        socialHandle: '',
        username: '',
        description: '',
        websiteUrl: ''
      })

      setPopupElements(prev => [...prev, response.data.popupElement])
      toast.success('Element created successfully')
    } catch (error) {
      console.error('Failed to create element:', error)
      console.error('Error details:', error.response?.data)
      toast.error('Failed to create element')
    }
  }

  const updateElementLocally = (elementId, updates) => {
    setPopupElements(prev => prev.map(element => 
      element.id === elementId ? { ...element, ...updates } : element
    ))
  }

  const saveElement = async (elementId) => {
    setSavingElements(prev => ({ ...prev, [elementId]: true }))
    
    try {
      const element = popupElements.find(el => el.id === elementId)
      if (!element) {
        throw new Error('Element not found')
      }

      console.log('🔍 Frontend - Element before save:', {
        id: element.id,
        name: element.name,
        username: element.username,
        usernameType: typeof element.username,
        hasUsername: 'username' in element
      });

      const updates = {
        name: element.name,
        title: element.title,
        socialHandle: element.socialHandle,
        username: element.username !== undefined ? element.username : '', // Always include username
        description: element.description,
        websiteUrl: element.websiteUrl,
        imageUrl: element.imageUrl
      }

      console.log('🔍 Frontend - Sending update with username:', updates.username);
      console.log('🔍 Frontend - Full updates object:', updates);

      const response = await api.put(`/popups/${elementId}`, updates)
      
      console.log('🔍 Frontend - Save response:', response.data);
      console.log('🔍 Frontend - Response username:', response.data?.popupElement?.username);
      
      // Update the element with the saved data from response (to get actual DB values)
      if (response.data?.popupElement) {
        setPopupElements(prev => prev.map(el => 
          el.id === elementId ? { 
            ...el, 
            ...response.data.popupElement,
            username: response.data.popupElement.username || '' // Ensure username is always set
          } : el
        ))
      } else {
        // Fallback to updates if response doesn't have popupElement
        setPopupElements(prev => prev.map(el => 
          el.id === elementId ? { ...el, ...updates } : el
        ))
      }
      
      // Exit edit mode
      setEditingElements(prev => ({ ...prev, [elementId]: false }))
      
      toast.success('Element saved successfully')
    } catch (error) {
      console.error('Failed to save element:', error)
      toast.error('Failed to save element')
    } finally {
      setSavingElements(prev => ({ ...prev, [elementId]: false }))
    }
  }

  const updateElement = async (elementId, updates) => {
    try {
      await api.put(`/popups/${elementId}`, updates)
      
      setPopupElements(prev => prev.map(element => 
        element.id === elementId ? { ...element, ...updates } : element
      ))
      
      // Only show toast for non-image updates to avoid spam
      if (!updates.imageUrl) {
        toast.success('Element updated successfully')
      }
    } catch (error) {
      console.error('Failed to update element:', error)
      toast.error('Failed to update element')
    }
  }

  const deleteElement = async (elementId) => {
    if (!window.confirm('Are you sure you want to delete this element?')) {
      return
    }

    try {
      await api.delete(`/popups/${elementId}`)
      setPopupElements(prev => prev.filter(element => element.id !== elementId))
      toast.success('Element deleted successfully')
    } catch (error) {
      console.error('Failed to delete element:', error)
      toast.error('Failed to delete element')
    }
  }


  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="spinner w-12 h-12 mx-auto mb-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          <p className="text-white">Loading popup editor...</p>
        </div>
      </div>
    )
  }

  // Get elements by type
  const hostElements = popupElements.filter(element => element.elementType === 'HOST')
  const promoElements = popupElements.filter(element => ['PROMO', 'SOCIAL', 'SHOP'].includes(element.elementType))

  return (
    <div className="bg-black text-white px-6 pb-6">

      {/* Host Cards Section */}
      <div className="mb-8 relative bg-[#1c1c1c] p-6">
        <div className="grid grid-cols-3 gap-6 ">
          {/* Existing Host Cards */}
          {hostElements.slice(0, 9).map((element) => (
            <div key={element.id} className="bg-[#323232]  border-white border-[1.5px] p-4 relative">
              <div className="flex items-start gap-4 mb-4">
                <label htmlFor={`image-${element.id}`} className="cursor-pointer">
                  <div className="w-24 h-24 bg-black border border-gray-500 flex items-center justify-center text-gray-400 text-xs text-center leading-tight">
                    {uploading ? (
                      <div className="text-center">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-1"></div>
                        <div className="text-xs">Uploading...</div>
                      </div>
                    ) : element.imageUrl ? (
                      <img 
                        src={getImageUrl(element.imageUrl)} 
                        alt={element.name}
                        className="w-full h-full object-cover "
                        onLoad={(e) => {
                          console.log('Image loaded successfully:', e.target.src)
                        }}
                        onError={(e) => {
                          console.error('Image failed to load:', e.target.src)
                          e.target.style.display = 'none'
                        }}
                      />
                    ) : (
                      <div className="text-center">
                        <div>CLICK</div>
                        <div>TO ADD</div>
                        <div>IMAGE</div>
                      </div>
                    )}
                  </div>
                </label>
                <div className="flex-1">
                  <input
                    type="text"
                    value={element.name}
                    onChange={(e) => updateElementLocally(element.id, { name: e.target.value })}
                    readOnly={!isEditing(element.id)}
                    className={`w-full bg-transparent text-white text-lg font-medium mb-1 border-none outline-none ${
                      isEditing(element.id) ? 'border-b border-gray-500' : ''
                    }`}
                    placeholder="Name"
                  />
                  <input
                    type="text"
                    value={element.title || ''}
                    onChange={(e) => updateElementLocally(element.id, { title: e.target.value })}
                    readOnly={!isEditing(element.id)}
                    className={`w-full bg-transparent text-gray-300 text-sm mb-1 border-none outline-none ${
                      isEditing(element.id) ? 'border-b border-gray-500' : ''
                    }`}
                    placeholder="Title"
                  />
                  <input
                    type="text"
                    value={element.socialHandle || ''}
                    onChange={(e) => updateElementLocally(element.id, { socialHandle: e.target.value })}
                    readOnly={!isEditing(element.id)}
                    className={`w-full bg-transparent text-gray-300 text-sm border-none outline-none ${
                      isEditing(element.id) ? 'border-b border-gray-500' : ''
                    }`}
                    placeholder="@handle"
                  />
                  <input
                    type="text"
                    value={element.username || ''}
                    onChange={(e) => updateElementLocally(element.id, { username: e.target.value })}
                    readOnly={!isEditing(element.id)}
                    className={`w-full bg-transparent text-gray-300 text-sm border-none outline-none ${
                      isEditing(element.id) ? 'border-b border-gray-500' : ''
                    }`}
                    placeholder="Username"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                {isEditing(element.id) ? (
                  <button
                    onClick={() => saveElement(element.id)}
                    disabled={isSaving(element.id)}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-3 py-1 text-sm flex items-center gap-1"
                  >
                    {isSaving(element.id) ? (
                      <>
                        <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                        Saving...
                      </>
                    ) : (
                      'SAVE'
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => toggleEdit(element.id)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 text-sm"
                  >
                    EDIT
                  </button>
                )}
                <button
                  onClick={() => deleteElement(element.id)}
                  className="text-white text-sm hover:text-gray-300 transition-colors"
                >
                  DELETE
                </button>
              </div>
              
              {/* Hidden file input */}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0]
                  if (file) {
                    handleImageUpload(file, element.id)
                  }
                }}
                className="hidden"
                id={`image-${element.id}`}
              />
            </div>
          ))}
          
        </div>
        
        {/* Add Host Button */}
        <div className="flex justify-end mt-4">
          <button
            onClick={() => createElement('HOST')}
            className="w-12 h-12 bg-gray-600 border border-gray-500 flex items-center justify-center hover:bg-gray-500 transition-colors"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </button>
        </div>
      </div>

      {/* Promotional Cards Section */}
      <div className="my-8 bg-[#1c1c1c] p-6">
        <div className="grid grid-cols-3 gap-6">
          {promoElements.map((element) => (
            <div key={element.id} className="bg-[#2a2a2a] border-[1.5px] border-white p-4 relative">
              <div className="flex items-start gap-4 mb-4">
                <label htmlFor={`promo-image-${element.id}`} className="cursor-pointer">
                  <div className="w-24 h-24 bg-black border border-gray-500 flex items-center justify-center text-gray-400 text-xs text-center leading-tight">
                    {uploading ? (
                      <div className="text-center">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-1"></div>
                        <div className="text-xs">Uploading...</div>
                      </div>
                    ) : element.imageUrl ? (
                      <img 
                        src={getImageUrl(element.imageUrl)} 
                        alt={element.name}
                        className="w-full h-full object-cover "
                        onLoad={(e) => {
                          console.log('Image loaded successfully:', e.target.src)
                        }}
                        onError={(e) => {
                          console.error('Image failed to load:', e.target.src)
                          e.target.style.display = 'none'
                        }}
                      />
                    ) : (
                      <div className="text-center">
                        <div>CLICK</div>
                        <div>TO ADD</div>
                        <div>IMAGE</div>
                      </div>
                    )}
                  </div>
                </label>
                <div className="flex-1">
                  <input
                    type="text"
                    value={element.name}
                    onChange={(e) => updateElementLocally(element.id, { name: e.target.value })}
                    readOnly={!isEditing(element.id)}
                    className={`w-full bg-transparent text-white text-lg font-medium mb-1 border-none outline-none ${
                      isEditing(element.id) ? 'border-b border-gray-500' : ''
                    }`}
                    placeholder="Name"
                  />
                  <input
                    type="text"
                    value={element.description || ''}
                    onChange={(e) => updateElementLocally(element.id, { description: e.target.value })}
                    readOnly={!isEditing(element.id)}
                    className={`w-full bg-transparent text-gray-300 text-sm mb-1 border-none outline-none ${
                      isEditing(element.id) ? 'border-b border-gray-500' : ''
                    }`}
                    placeholder="Description"
                  />
                  <input
                    type="text"
                    value={element.username || ''}
                    onChange={(e) => updateElementLocally(element.id, { username: e.target.value })}
                    readOnly={!isEditing(element.id)}
                    className={`w-full bg-transparent text-gray-300 text-sm mb-1 border-none outline-none ${
                      isEditing(element.id) ? 'border-b border-gray-500' : ''
                    }`}
                    placeholder="Username"
                  />
                  {element.elementType === 'SHOP' && (
                    <input
                      type="text"
                      value={element.websiteUrl || ''}
                      onChange={(e) => updateElementLocally(element.id, { websiteUrl: e.target.value })}
                      readOnly={!isEditing(element.id)}
                      className={`w-full bg-transparent text-red-400 text-sm border-none outline-none ${
                        isEditing(element.id) ? 'border-b border-gray-500' : ''
                      }`}
                      placeholder="Website URL"
                    />
                  )}
                  {element.elementType === 'SOCIAL' && (
                    <input
                      type="text"
                      value={element.socialHandle || ''}
                      onChange={(e) => updateElementLocally(element.id, { socialHandle: e.target.value })}
                      readOnly={!isEditing(element.id)}
                      className={`w-full bg-transparent text-gray-300 text-sm border-none outline-none ${
                        isEditing(element.id) ? 'border-b border-gray-500' : ''
                      }`}
                      placeholder="@handle"
                    />
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                {isEditing(element.id) ? (
                  <button
                    onClick={() => saveElement(element.id)}
                    disabled={isSaving(element.id)}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-3 py-1 text-sm flex items-center gap-1"
                  >
                    {isSaving(element.id) ? (
                      <>
                        <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                        Saving...
                      </>
                    ) : (
                      'SAVE'
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => toggleEdit(element.id)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 text-sm"
                  >
                    EDIT
                  </button>
                )}
                <button
                  onClick={() => deleteElement(element.id)}
                  className="text-white text-sm hover:text-gray-300 transition-colors"
                >
                  DELETE
                </button>
              </div>
              
              {/* File input for image upload */}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0]
                  if (file) {
                    handleImageUpload(file, element.id)
                  }
                }}
                className="hidden"
                id={`promo-image-${element.id}`}
              />
            </div>
          ))}
        </div>
        
        {/* Add Promotional Card Buttons */}
        <div className="flex gap-4 mt-4">
          <button
            onClick={() => createElement('PROMO')}
            className="bg-transparent border border-white text-white px-4 py-2 hover:bg-white hover:text-black transition-colors"
          >
            + Add Promo
          </button>
          <button
            onClick={() => createElement('SHOP')}
            className="bg-transparent border border-white text-white px-4 py-2 hover:bg-white hover:text-black transition-colors"
          >
            + Add Shop
          </button>
          <button
            onClick={() => createElement('SOCIAL')}
            className="bg-transparent border border-white text-white px-4 py-2 hover:bg-white hover:text-black transition-colors"
          >
            + Add Social
          </button>
        </div>
      </div>
    </div>
  )
}

export default PopupEditor